import * as i0 from '@angular/core';
import { Directive, Input, Injectable, Pipe, Component, EventEmitter, Output, ContentChildren, ContentChild, NgModule } from '@angular/core';
import * as i2 from '@angular/common';
import { CommonModule } from '@angular/common';
import Feature from 'ol/Feature';

class ValueTemplateDirective {
    constructor(templateRef) {
        this.templateRef = templateRef;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.3", ngImport: i0, type: ValueTemplateDirective, deps: [{ token: i0.TemplateRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.2.3", type: ValueTemplateDirective, selector: "[ggcTemplateKey]", inputs: { ggcTemplateKey: "ggcTemplateKey" }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.3", ngImport: i0, type: ValueTemplateDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: "[ggcTemplateKey]",
                }]
        }], ctorParameters: function () { return [{ type: i0.TemplateRef }]; }, propDecorators: { ggcTemplateKey: [{
                type: Input
            }] } });

var FeatureInfoDisplayType;
(function (FeatureInfoDisplayType) {
    FeatureInfoDisplayType["TABLE"] = "table-view";
    FeatureInfoDisplayType["LIST"] = "list-view";
})(FeatureInfoDisplayType || (FeatureInfoDisplayType = {}));

var FeatureInfoComponentEventType;
(function (FeatureInfoComponentEventType) {
    FeatureInfoComponentEventType["SELECTEDOBJECT"] = "selectedObject";
    FeatureInfoComponentEventType["SELECTEDTAB"] = "selectedTab";
})(FeatureInfoComponentEventType || (FeatureInfoComponentEventType = {}));
class FeatureInfoComponentEvent {
    /**
     * @param type FeatureInfoComponentEventType
     * @param message de gebruiker heeft een andere feature gekozen,  ...
     * @param value het actuele object of FeatureInfoCollection
     */
    constructor(type, message, value) {
        this.type = type;
        this.message = message;
        this.value = value;
    }
}

class FeatureInfoConfigService {
    setConfig(sortFilterConfigs) {
        this.sortFilterConfigs = sortFilterConfigs;
    }
    setCustomFeatureInfo(customFeatureInfo) {
        this.customFeatureInfo = customFeatureInfo;
    }
    setSortTabFunction(sortTabFunction) {
        this.sortTabFunction = sortTabFunction;
    }
    sortTabs(data) {
        data.sort(this.sortTabFunction.bind(this));
    }
    filterAndSortAttributes(layerName, featureProperties) {
        const config = this.sortFilterConfigs
            ? this.sortFilterConfigs.find((conf) => conf.layerName === layerName)
            : undefined;
        if (!config && !this.customFeatureInfo) {
            return featureProperties;
        }
        const filteredFeatureProperties = [];
        featureProperties.forEach((feature) => {
            // get array of keys to sort and filter the keys
            let featureKeys = Object.keys(feature);
            if (config) {
                featureKeys = this.excludeAttributes(config, featureKeys);
                featureKeys = this.sortAttributes(config, featureKeys);
                featureKeys = this.checkUnsortedAttributes(config, featureKeys);
            }
            // loop over keys to create a new object with sorted and filtered properties
            const sortedFeatureProperties = {};
            featureKeys.forEach((key) => {
                sortedFeatureProperties[this.replaceName(key)] = feature[key];
            });
            filteredFeatureProperties.push(sortedFeatureProperties);
        });
        return filteredFeatureProperties;
    }
    checkForCustomValues(currentFeature, objectKeys) {
        if (!this.customFeatureInfo) {
            return currentFeature;
        }
        const displayFeature = Object.assign({}, currentFeature);
        this.customFeatureInfo.forEach((featureInfoCustom, customAttributesValuesKey) => {
            if (featureInfoCustom) {
                const customAttributeValueFunction = featureInfoCustom.getCustomAttributeValueFunction();
                if (customAttributeValueFunction) {
                    // path voor als er een customfunctie is, maar geen customname
                    let nameMatchFound = this.findMatchToReplace(customAttributesValuesKey, objectKeys);
                    if (!nameMatchFound) {
                        // path voor als er een customfunctie is en ook een customname
                        nameMatchFound = this.findMatchToReplace(featureInfoCustom.getCustomAttributeName(), objectKeys);
                    }
                    if (nameMatchFound) {
                        this.replaceValue(nameMatchFound, customAttributeValueFunction, currentFeature, displayFeature);
                    }
                }
            }
        });
        return displayFeature;
    }
    excludeAttributes(config, featureKeys) {
        if (config.excludeAttributes) {
            // remove the keys from excludedAttributes
            featureKeys = featureKeys.filter((key) => {
                const index = config.excludeAttributes
                    ? config.excludeAttributes.indexOf(key)
                    : -1;
                return index < 0;
            });
        }
        return featureKeys;
    }
    sortAttributes(config, featureKeys) {
        if (config.attributeOrder) {
            // sort the keys in attributeOrder
            featureKeys.sort((a, b) => {
                const indexA = config.attributeOrder
                    ? config.attributeOrder.indexOf(a)
                    : -1;
                const indexB = config.attributeOrder
                    ? config.attributeOrder.indexOf(b)
                    : -1;
                if (indexA === -1) {
                    return 1;
                }
                if (indexB === -1) {
                    return -1;
                }
                return indexA - indexB;
            });
        }
        return featureKeys;
    }
    checkUnsortedAttributes(config, featureKeys) {
        if (config.attributeOrder && config.hideUnorderedAttributes) {
            // hide unordered keys
            featureKeys = featureKeys.filter((key) => {
                const index = config.attributeOrder
                    ? config.attributeOrder.indexOf(key)
                    : -1;
                return index >= 0;
            });
        }
        return featureKeys;
    }
    replaceName(oldKey) {
        if (this.customFeatureInfo) {
            const featureInfocustom = this.customFeatureInfo.get(oldKey);
            if (featureInfocustom) {
                const customKey = featureInfocustom.getCustomAttributeName();
                return customKey !== undefined ? customKey : oldKey;
            }
        }
        return oldKey;
    }
    replaceValue(key, customFunction, currentFeature, displayFeature) {
        const currentFeatureValue = currentFeature[key];
        try {
            displayFeature[key] = customFunction(currentFeatureValue);
        }
        catch (error) {
            // Als er een probleem optreedt tijdens het veranderen van de value wordt de oude value teruggezet
            displayFeature[key] = currentFeature[key];
            console.warn(error);
        }
    }
    findMatchToReplace(fieldToFind, objectKeys) {
        return objectKeys.find((keyInObject) => keyInObject === fieldToFind);
    }
    sortTabFunction(a, b) {
        if (this.sortFilterConfigs) {
            // config wordt alleen gevonden wanneer de layerName gevonden wordt EN wanneer de config een tabIndex heeft
            const configA = this.sortFilterConfigs.find((config) => config.layerName === a.layerName && !!config.tabIndex);
            const configB = this.sortFilterConfigs.find((config) => config.layerName === b.layerName && !!config.tabIndex);
            const tabIndexA = configA ? configA.tabIndex : undefined;
            const tabIndexB = configB ? configB.tabIndex : undefined;
            if (tabIndexA && tabIndexB) {
                return tabIndexA - tabIndexB;
            }
            else if (!tabIndexA && tabIndexB) {
                return 1;
            }
            else if (tabIndexA && !tabIndexB) {
                return -1;
            }
        }
        return 0;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.3", ngImport: i0, type: FeatureInfoConfigService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.3", ngImport: i0, type: FeatureInfoConfigService, providedIn: "root" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.3", ngImport: i0, type: FeatureInfoConfigService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: "root",
                }]
        }] });

class FeatureInfoKeyValue {
    static objectKeys(feature, hideEmptyFields) {
        if (!feature) {
            return [];
        }
        const keys = Object.keys(feature);
        if (hideEmptyFields) {
            const keysNotEmpty = [];
            keys.forEach((key) => {
                if (feature[key] !== null && feature[key] !== "") {
                    keysNotEmpty.push(key);
                }
            });
            return keysNotEmpty;
        }
        return keys;
    }
}

class FeatureKeysPipe {
    constructor() {
        this.keysNotReturn = "geometry";
    }
    transform(keyArray) {
        return keyArray.filter((key) => key !== this.keysNotReturn);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.3", ngImport: i0, type: FeatureKeysPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "16.2.3", ngImport: i0, type: FeatureKeysPipe, name: "featureinfokeys" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.3", ngImport: i0, type: FeatureKeysPipe, decorators: [{
            type: Pipe,
            args: [{ name: "featureinfokeys" }]
        }] });

class FeatureInfoDisplayTableComponent {
    constructor(featureInfoConfigService) {
        this.featureInfoConfigService = featureInfoConfigService;
        this.valueTemplates = new Map();
    }
    ngOnInit() {
        this.prepareForDisplay();
    }
    ngOnChanges(changes) {
        if (changes.currentFeature && !changes.currentFeature.firstChange) {
            this.prepareForDisplay();
        }
    }
    prepareForDisplay() {
        this.objectKeys = FeatureInfoKeyValue.objectKeys(this.currentFeature, this.hideEmptyFields);
        this.displayFeature = this.featureInfoConfigService.checkForCustomValues(this.currentFeature, this.objectKeys);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.3", ngImport: i0, type: FeatureInfoDisplayTableComponent, deps: [{ token: FeatureInfoConfigService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.3", type: FeatureInfoDisplayTableComponent, selector: "ggc-feature-info-display-table", inputs: { currentFeature: "currentFeature", hideEmptyFields: "hideEmptyFields", valueTemplates: "valueTemplates" }, usesOnChanges: true, ngImport: i0, template: "<table class=\"ggc-fi-table table\">\n  <caption class=\"sr-only\">\n    Presentatie van feature data in tabelvorm\n  </caption>\n  <thead class=\"ggc-fi-thead\">\n    <tr>\n      <th class=\"ggc-fi-col-first\" scope=\"col\">Naam</th>\n      <th class=\"ggc-fi-col-second\" scope=\"col\">Waarde</th>\n    </tr>\n  </thead>\n  <tbody class=\"ggc-fi-tbody\">\n    <tr *ngFor=\"let key of objectKeys | featureinfokeys\">\n      <td>{{ key }}</td>\n      <td>\n        <ng-container\n          class=\"ggc-fi-list-content\"\n          [ngTemplateOutlet]=\"\n            valueTemplates.get(key)\n              ? valueTemplates.get(key)\n              : defaultValueTemplate\n          \"\n          [ngTemplateOutletContext]=\"{\n            $implicit: key,\n            value: displayFeature[key],\n            properties: displayFeature\n          }\"\n        ></ng-container>\n        <ng-template #defaultValueTemplate>{{\n          displayFeature[key]\n        }}</ng-template>\n      </td>\n    </tr>\n  </tbody>\n</table>\n", styles: ["dd{white-space:pre-line}\n"], dependencies: [{ kind: "directive", type: i2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i2.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "pipe", type: FeatureKeysPipe, name: "featureinfokeys" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.3", ngImport: i0, type: FeatureInfoDisplayTableComponent, decorators: [{
            type: Component,
            args: [{ selector: "ggc-feature-info-display-table", template: "<table class=\"ggc-fi-table table\">\n  <caption class=\"sr-only\">\n    Presentatie van feature data in tabelvorm\n  </caption>\n  <thead class=\"ggc-fi-thead\">\n    <tr>\n      <th class=\"ggc-fi-col-first\" scope=\"col\">Naam</th>\n      <th class=\"ggc-fi-col-second\" scope=\"col\">Waarde</th>\n    </tr>\n  </thead>\n  <tbody class=\"ggc-fi-tbody\">\n    <tr *ngFor=\"let key of objectKeys | featureinfokeys\">\n      <td>{{ key }}</td>\n      <td>\n        <ng-container\n          class=\"ggc-fi-list-content\"\n          [ngTemplateOutlet]=\"\n            valueTemplates.get(key)\n              ? valueTemplates.get(key)\n              : defaultValueTemplate\n          \"\n          [ngTemplateOutletContext]=\"{\n            $implicit: key,\n            value: displayFeature[key],\n            properties: displayFeature\n          }\"\n        ></ng-container>\n        <ng-template #defaultValueTemplate>{{\n          displayFeature[key]\n        }}</ng-template>\n      </td>\n    </tr>\n  </tbody>\n</table>\n", styles: ["dd{white-space:pre-line}\n"] }]
        }], ctorParameters: function () { return [{ type: FeatureInfoConfigService }]; }, propDecorators: { currentFeature: [{
                type: Input
            }], hideEmptyFields: [{
                type: Input
            }], valueTemplates: [{
                type: Input
            }] } });

class FeatureInfoDisplayListComponent {
    constructor(featureInfoConfigService) {
        this.featureInfoConfigService = featureInfoConfigService;
        this.valueTemplates = new Map();
    }
    ngOnInit() {
        this.prepareForDisplay();
    }
    ngOnChanges(changes) {
        if (changes.currentFeature && !changes.currentFeature.firstChange) {
            this.prepareForDisplay();
        }
    }
    prepareForDisplay() {
        this.objectKeys = FeatureInfoKeyValue.objectKeys(this.currentFeature, this.hideEmptyFields);
        this.displayFeature = this.featureInfoConfigService.checkForCustomValues(this.currentFeature, this.objectKeys);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.3", ngImport: i0, type: FeatureInfoDisplayListComponent, deps: [{ token: FeatureInfoConfigService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.3", type: FeatureInfoDisplayListComponent, selector: "ggc-feature-info-display-list", inputs: { currentFeature: "currentFeature", hideEmptyFields: "hideEmptyFields", valueTemplates: "valueTemplates" }, usesOnChanges: true, ngImport: i0, template: "<dl class=\"ggc-fi-list-item\">\n  <ng-container *ngFor=\"let key of objectKeys | featureinfokeys\">\n    <dt class=\"ggc-fi-list-title\">{{ key }}</dt>\n    <dd class=\"ggc-fi-list-content\">\n      <ng-container\n        [ngTemplateOutlet]=\"\n          valueTemplates.get(key)\n            ? valueTemplates.get(key)\n            : defaultValueTemplate\n        \"\n        [ngTemplateOutletContext]=\"{\n          $implicit: key,\n          value: displayFeature[key],\n          properties: displayFeature\n        }\"\n      ></ng-container>\n      <ng-template #defaultValueTemplate>{{ displayFeature[key] }}</ng-template>\n    </dd>\n  </ng-container>\n</dl>\n", styles: ["dd{white-space:pre-line}\n"], dependencies: [{ kind: "directive", type: i2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i2.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "pipe", type: FeatureKeysPipe, name: "featureinfokeys" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.3", ngImport: i0, type: FeatureInfoDisplayListComponent, decorators: [{
            type: Component,
            args: [{ selector: "ggc-feature-info-display-list", template: "<dl class=\"ggc-fi-list-item\">\n  <ng-container *ngFor=\"let key of objectKeys | featureinfokeys\">\n    <dt class=\"ggc-fi-list-title\">{{ key }}</dt>\n    <dd class=\"ggc-fi-list-content\">\n      <ng-container\n        [ngTemplateOutlet]=\"\n          valueTemplates.get(key)\n            ? valueTemplates.get(key)\n            : defaultValueTemplate\n        \"\n        [ngTemplateOutletContext]=\"{\n          $implicit: key,\n          value: displayFeature[key],\n          properties: displayFeature\n        }\"\n      ></ng-container>\n      <ng-template #defaultValueTemplate>{{ displayFeature[key] }}</ng-template>\n    </dd>\n  </ng-container>\n</dl>\n", styles: ["dd{white-space:pre-line}\n"] }]
        }], ctorParameters: function () { return [{ type: FeatureInfoConfigService }]; }, propDecorators: { currentFeature: [{
                type: Input
            }], hideEmptyFields: [{
                type: Input
            }], valueTemplates: [{
                type: Input
            }] } });

class FeatureInfoComponent {
    constructor(featureInfoConfigService) {
        this.featureInfoConfigService = featureInfoConfigService;
        this.showEmptyMessage = true;
        this.featureInfoDisplayType = FeatureInfoDisplayType.TABLE;
        this.hidePagerWithOneFeature = false;
        this.pagerPrevious = "<";
        this.pagerNext = ">";
        this.hideEmptyFields = false;
        this.events = new EventEmitter();
        this.customValueTemplates = new Map();
        this.currentFeatureIndex = 0;
        this.emptyInfo = "Geen informatie beschikbaar";
        this.featureInfoDisplayTypeEnum = FeatureInfoDisplayType;
    }
    ngAfterContentInit() {
        this.templates.forEach((template) => {
            if (template.ggcTemplateKey instanceof Array) {
                template.ggcTemplateKey.forEach((templateKey) => this.customValueTemplates.set(templateKey, template.templateRef));
            }
            else {
                this.customValueTemplates.set(template.ggcTemplateKey, template.templateRef);
            }
        });
    }
    ngOnChanges(changes) {
        // first, filter and sort features or clear displayFeatures
        if (changes.featureInfoCollection ||
            changes.customAttributeNamesAndValues) {
            if (!this.featureInfoCollection) {
                this.displayFeaturesProperties = undefined;
            }
            else {
                if (this.customAttributeNamesAndValues) {
                    this.featureInfoConfigService.setCustomFeatureInfo(this.customAttributeNamesAndValues);
                }
                const featuresProperties = this.getPropertiesFromFeatures(this.featureInfoCollection.features);
                this.displayFeaturesProperties =
                    this.featureInfoConfigService.filterAndSortAttributes(this.featureInfoCollection.layerName, featuresProperties);
            }
            if (this.displayFeaturesProperties &&
                this.displayFeaturesProperties.length > 0) {
                this.currentFeatureIndex = 0;
                this.setCurrentFeature();
            }
            else {
                this.currentFeatureIndex = -1;
                this.currentFeature = null;
                this.events.next(new FeatureInfoComponentEvent(FeatureInfoComponentEventType.SELECTEDOBJECT, "Het huidige weergegeven object.", undefined));
            }
            this.pagerIsHidden = this.hidePager();
        }
    }
    goToPreviousFeature() {
        if (this.hasPreviousFeature()) {
            this.currentFeatureIndex--;
            this.setCurrentFeature();
        }
    }
    goToNextFeature() {
        if (this.hasNextFeature()) {
            this.currentFeatureIndex++;
            this.setCurrentFeature();
        }
    }
    hasNextFeature() {
        const length = this.displayFeaturesProperties
            ? this.displayFeaturesProperties.length
            : -1;
        if (length > 0) {
            return this.currentFeatureIndex < length - 1;
        }
        return false;
    }
    hasPreviousFeature() {
        if (this.displayFeaturesProperties &&
            this.displayFeaturesProperties.length > 1) {
            return this.currentFeatureIndex > 0;
        }
        return false;
    }
    setCurrentFeature() {
        this.currentFeature = this.displayFeaturesProperties
            ? this.displayFeaturesProperties[this.currentFeatureIndex]
            : null;
        const featureForEvent = this.featureInfoCollection
            ? this.featureInfoCollection.features[this.currentFeatureIndex]
            : undefined;
        const featureInfoComponentEvent = new FeatureInfoComponentEvent(FeatureInfoComponentEventType.SELECTEDOBJECT, "Het huidige weergegeven object.", featureForEvent);
        this.events.next(featureInfoComponentEvent);
    }
    getPropertiesFromFeatures(features) {
        const arrayContainingFeatureProperties = [];
        features.forEach((feature) => {
            if (feature instanceof Feature) {
                arrayContainingFeatureProperties.push(feature.getProperties());
            }
            else {
                arrayContainingFeatureProperties.push(feature);
            }
        });
        return arrayContainingFeatureProperties;
    }
    hidePager() {
        return (this.hidePagerWithOneFeature &&
            this.displayFeaturesProperties !== undefined &&
            this.displayFeaturesProperties.length === 1);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.3", ngImport: i0, type: FeatureInfoComponent, deps: [{ token: FeatureInfoConfigService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.3", type: FeatureInfoComponent, selector: "ggc-feature-info", inputs: { featureInfoCollection: "featureInfoCollection", showEmptyMessage: "showEmptyMessage", featureInfoDisplayType: "featureInfoDisplayType", hidePagerWithOneFeature: "hidePagerWithOneFeature", pagerPrevious: "pagerPrevious", pagerNext: "pagerNext", customAttributeNamesAndValues: "customAttributeNamesAndValues", hideEmptyFields: "hideEmptyFields" }, outputs: { events: "events" }, queries: [{ propertyName: "templates", predicate: ValueTemplateDirective }], usesOnChanges: true, ngImport: i0, template: "<ng-container *ngIf=\"currentFeature\">\n  <nav *ngIf=\"!pagerIsHidden\" aria-label=\"Paginering navigatie\">\n    <ul class=\"pagination ggc-fi-pager\">\n      <li class=\"ggc-fi-pager-previous\">\n        <button\n          (click)=\"goToPreviousFeature()\"\n          class=\"btn btn-sm\"\n          [disabled]=\"!hasPreviousFeature()\"\n          title=\"Vorige\"\n          aria-label=\"Vorige\"\n        >\n          {{ pagerPrevious }}\n        </button>\n      </li>\n      <li class=\"ggc-fi-pager-value\">\n        {{ currentFeatureIndex + 1 }} van\n        {{ displayFeaturesProperties ? displayFeaturesProperties.length : \"\" }}\n      </li>\n      <li class=\"ggc-fi-pager-next\">\n        <button\n          (click)=\"goToNextFeature()\"\n          class=\"btn btn-sm\"\n          [disabled]=\"!hasNextFeature()\"\n          title=\"Volgende\"\n          aria-label=\"Volgende\"\n        >\n          {{ pagerNext }}\n        </button>\n      </li>\n    </ul>\n  </nav>\n  <ng-container [ngSwitch]=\"featureInfoDisplayType\">\n    <ggc-feature-info-display-table\n      *ngSwitchCase=\"featureInfoDisplayTypeEnum.TABLE\"\n      [hideEmptyFields]=\"hideEmptyFields\"\n      [currentFeature]=\"currentFeature\"\n      [valueTemplates]=\"customValueTemplates\"\n    ></ggc-feature-info-display-table>\n    <ggc-feature-info-display-list\n      *ngSwitchCase=\"featureInfoDisplayTypeEnum.LIST\"\n      [hideEmptyFields]=\"hideEmptyFields\"\n      [currentFeature]=\"currentFeature\"\n      [valueTemplates]=\"customValueTemplates\"\n    ></ggc-feature-info-display-list>\n  </ng-container>\n</ng-container>\n<ng-container *ngIf=\"showEmptyMessage && !currentFeature\">\n  <span class=\"ggc-fi-empty\">{{ emptyInfo }}</span>\n</ng-container>\n<ng-content></ng-content>\n", styles: [".ggc-fi-pager{margin:10px 10px 0}\n"], dependencies: [{ kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2.NgSwitch, selector: "[ngSwitch]", inputs: ["ngSwitch"] }, { kind: "directive", type: i2.NgSwitchCase, selector: "[ngSwitchCase]", inputs: ["ngSwitchCase"] }, { kind: "component", type: FeatureInfoDisplayTableComponent, selector: "ggc-feature-info-display-table", inputs: ["currentFeature", "hideEmptyFields", "valueTemplates"] }, { kind: "component", type: FeatureInfoDisplayListComponent, selector: "ggc-feature-info-display-list", inputs: ["currentFeature", "hideEmptyFields", "valueTemplates"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.3", ngImport: i0, type: FeatureInfoComponent, decorators: [{
            type: Component,
            args: [{ selector: "ggc-feature-info", template: "<ng-container *ngIf=\"currentFeature\">\n  <nav *ngIf=\"!pagerIsHidden\" aria-label=\"Paginering navigatie\">\n    <ul class=\"pagination ggc-fi-pager\">\n      <li class=\"ggc-fi-pager-previous\">\n        <button\n          (click)=\"goToPreviousFeature()\"\n          class=\"btn btn-sm\"\n          [disabled]=\"!hasPreviousFeature()\"\n          title=\"Vorige\"\n          aria-label=\"Vorige\"\n        >\n          {{ pagerPrevious }}\n        </button>\n      </li>\n      <li class=\"ggc-fi-pager-value\">\n        {{ currentFeatureIndex + 1 }} van\n        {{ displayFeaturesProperties ? displayFeaturesProperties.length : \"\" }}\n      </li>\n      <li class=\"ggc-fi-pager-next\">\n        <button\n          (click)=\"goToNextFeature()\"\n          class=\"btn btn-sm\"\n          [disabled]=\"!hasNextFeature()\"\n          title=\"Volgende\"\n          aria-label=\"Volgende\"\n        >\n          {{ pagerNext }}\n        </button>\n      </li>\n    </ul>\n  </nav>\n  <ng-container [ngSwitch]=\"featureInfoDisplayType\">\n    <ggc-feature-info-display-table\n      *ngSwitchCase=\"featureInfoDisplayTypeEnum.TABLE\"\n      [hideEmptyFields]=\"hideEmptyFields\"\n      [currentFeature]=\"currentFeature\"\n      [valueTemplates]=\"customValueTemplates\"\n    ></ggc-feature-info-display-table>\n    <ggc-feature-info-display-list\n      *ngSwitchCase=\"featureInfoDisplayTypeEnum.LIST\"\n      [hideEmptyFields]=\"hideEmptyFields\"\n      [currentFeature]=\"currentFeature\"\n      [valueTemplates]=\"customValueTemplates\"\n    ></ggc-feature-info-display-list>\n  </ng-container>\n</ng-container>\n<ng-container *ngIf=\"showEmptyMessage && !currentFeature\">\n  <span class=\"ggc-fi-empty\">{{ emptyInfo }}</span>\n</ng-container>\n<ng-content></ng-content>\n", styles: [".ggc-fi-pager{margin:10px 10px 0}\n"] }]
        }], ctorParameters: function () { return [{ type: FeatureInfoConfigService }]; }, propDecorators: { featureInfoCollection: [{
                type: Input
            }], showEmptyMessage: [{
                type: Input
            }], featureInfoDisplayType: [{
                type: Input
            }], hidePagerWithOneFeature: [{
                type: Input
            }], pagerPrevious: [{
                type: Input
            }], pagerNext: [{
                type: Input
            }], customAttributeNamesAndValues: [{
                type: Input
            }], hideEmptyFields: [{
                type: Input
            }], events: [{
                type: Output
            }], templates: [{
                type: ContentChildren,
                args: [ValueTemplateDirective]
            }] } });

class FeatureInfoTabsComponent {
    constructor(featureInfoConfigService) {
        this.featureInfoConfigService = featureInfoConfigService;
        this.showEmptyTabs = false; // default = false, lege tabbladen worden default niet getoond
        this.events = new EventEmitter();
    }
    ngAfterContentInit() {
        if (this.tabTemplate) {
            this.tabComponent = this.tabTemplate.templateRef;
        }
    }
    ngOnInit() {
        this.onDataUpdate();
    }
    ngOnChanges(changes) {
        if (changes.featureInfoCollectionArray) {
            this.onDataUpdate();
        }
    }
    onTabClicked(tab) {
        this.lastSelectedTabOnClick = tab;
        this.setActiveTab(tab);
    }
    onDataUpdate() {
        // create copy of featureInfoCollectionArray and check empty tabs
        this.featureInfoCollectionArrayInternal = !this.featureInfoCollectionArray
            ? []
            : [...this.featureInfoCollectionArray];
        this.checkShowEmptyTabs();
        if (this.featureInfoCollectionArrayInternal.length === 0) {
            this.events.emit(new FeatureInfoComponentEvent(FeatureInfoComponentEventType.SELECTEDTAB, "Het huidige weergegeven tabblad.", undefined));
        }
        else {
            // this.featureInfoCollectionArrayInternal.length > 0
            this.featureInfoConfigService.sortTabs(this.featureInfoCollectionArrayInternal);
            this.setActiveTab(this.lastSelectedTabOnClick);
        }
    }
    setActiveTab(layerName) {
        let idx = this.featureInfoCollectionArrayInternal.findIndex((tabFeatureInfo) => tabFeatureInfo.layerName === layerName);
        if (idx === -1) {
            idx = 0;
        }
        this.selectedTabFeatureInfo = this.featureInfoCollectionArrayInternal[idx];
        this.selectedTab = this.selectedTabFeatureInfo.layerName;
        this.events.emit(new FeatureInfoComponentEvent(FeatureInfoComponentEventType.SELECTEDTAB, "Het huidige weergegeven tabblad.", this.selectedTabFeatureInfo));
    }
    checkShowEmptyTabs() {
        if (!this.showEmptyTabs) {
            this.featureInfoCollectionArrayInternal =
                this.featureInfoCollectionArrayInternal.filter((tabFeatureInfo) => {
                    return tabFeatureInfo.features.length > 0;
                });
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.3", ngImport: i0, type: FeatureInfoTabsComponent, deps: [{ token: FeatureInfoConfigService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.3", type: FeatureInfoTabsComponent, selector: "ggc-feature-info-tabs", inputs: { featureInfoCollectionArray: "featureInfoCollectionArray", showEmptyTabs: "showEmptyTabs" }, outputs: { events: "events" }, queries: [{ propertyName: "tabTemplate", first: true, predicate: ValueTemplateDirective }], usesOnChanges: true, ngImport: i0, template: "<div\n  class=\"ggc-fi-tab\"\n  *ngIf=\"\n    featureInfoCollectionArrayInternal &&\n    featureInfoCollectionArrayInternal.length > 0\n  \"\n>\n  <ng-container *ngFor=\"let info of featureInfoCollectionArrayInternal\">\n    <button\n      [ngClass]=\"{\n        active: info.layerName === selectedTab,\n        inactive: info.layerName !== selectedTab\n      }\"\n      (click)=\"onTabClicked(info.layerName)\"\n    >\n      <ng-container\n        [ngTemplateOutlet]=\"tabComponent ? tabComponent : defaultValueTemplate\"\n        [ngTemplateOutletContext]=\"{\n          layerName: info.layerName,\n          active: info.layerName === selectedTab\n        }\"\n      ></ng-container>\n      <ng-template #defaultValueTemplate>{{ info.layerName }}</ng-template>\n    </button>\n  </ng-container>\n</div>\n<div class=\"ggc-fi-tab-content\">\n  <ng-content></ng-content>\n</div>\n", styles: [".ggc-fi-tab{background-color:var(--white, white);border-bottom:1px solid var(--gray-18, #ccc);margin:0;overflow:hidden}.ggc-fi-tab button{background-color:var(--gray-01, #f1f1f1);border-bottom:none;border-left:none;border-top:1px solid var(--white, white);border-right:1px solid var(--white, white);padding:4px 14px;transition:.4s}.ggc-fi-tab button:hover{background-color:var(--gray-18, #ddd)}.ggc-fi-tab button.active{background-color:var(--primary, #007395);color:var(--white, white)}.ggc-fi-tab button.inactive{color:var(--gray-43, #888)}\n"], dependencies: [{ kind: "directive", type: i2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.3", ngImport: i0, type: FeatureInfoTabsComponent, decorators: [{
            type: Component,
            args: [{ selector: "ggc-feature-info-tabs", template: "<div\n  class=\"ggc-fi-tab\"\n  *ngIf=\"\n    featureInfoCollectionArrayInternal &&\n    featureInfoCollectionArrayInternal.length > 0\n  \"\n>\n  <ng-container *ngFor=\"let info of featureInfoCollectionArrayInternal\">\n    <button\n      [ngClass]=\"{\n        active: info.layerName === selectedTab,\n        inactive: info.layerName !== selectedTab\n      }\"\n      (click)=\"onTabClicked(info.layerName)\"\n    >\n      <ng-container\n        [ngTemplateOutlet]=\"tabComponent ? tabComponent : defaultValueTemplate\"\n        [ngTemplateOutletContext]=\"{\n          layerName: info.layerName,\n          active: info.layerName === selectedTab\n        }\"\n      ></ng-container>\n      <ng-template #defaultValueTemplate>{{ info.layerName }}</ng-template>\n    </button>\n  </ng-container>\n</div>\n<div class=\"ggc-fi-tab-content\">\n  <ng-content></ng-content>\n</div>\n", styles: [".ggc-fi-tab{background-color:var(--white, white);border-bottom:1px solid var(--gray-18, #ccc);margin:0;overflow:hidden}.ggc-fi-tab button{background-color:var(--gray-01, #f1f1f1);border-bottom:none;border-left:none;border-top:1px solid var(--white, white);border-right:1px solid var(--white, white);padding:4px 14px;transition:.4s}.ggc-fi-tab button:hover{background-color:var(--gray-18, #ddd)}.ggc-fi-tab button.active{background-color:var(--primary, #007395);color:var(--white, white)}.ggc-fi-tab button.inactive{color:var(--gray-43, #888)}\n"] }]
        }], ctorParameters: function () { return [{ type: FeatureInfoConfigService }]; }, propDecorators: { featureInfoCollectionArray: [{
                type: Input
            }], showEmptyTabs: [{
                type: Input
            }], events: [{
                type: Output
            }], tabTemplate: [{
                type: ContentChild,
                args: [ValueTemplateDirective, { descendants: false }]
            }] } });

class GgcFeatureInfoModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.3", ngImport: i0, type: GgcFeatureInfoModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.3", ngImport: i0, type: GgcFeatureInfoModule, declarations: [FeatureInfoComponent,
            FeatureKeysPipe,
            FeatureInfoTabsComponent,
            FeatureInfoDisplayTableComponent,
            FeatureInfoDisplayListComponent,
            ValueTemplateDirective], imports: [CommonModule], exports: [FeatureInfoComponent,
            FeatureInfoTabsComponent,
            ValueTemplateDirective] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.3", ngImport: i0, type: GgcFeatureInfoModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.3", ngImport: i0, type: GgcFeatureInfoModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [
                        FeatureInfoComponent,
                        FeatureKeysPipe,
                        FeatureInfoTabsComponent,
                        FeatureInfoDisplayTableComponent,
                        FeatureInfoDisplayListComponent,
                        ValueTemplateDirective,
                    ],
                    exports: [
                        FeatureInfoComponent,
                        FeatureInfoTabsComponent,
                        ValueTemplateDirective,
                    ],
                }]
        }] });

class FeatureInfoCollection {
    constructor(layerName, features) {
        this.layerName = layerName;
        this.features = features;
    }
}

class CustomFeatureInfo {
    constructor(customFeatureInfoOptions) {
        this.customFeatureInfoOptions = customFeatureInfoOptions;
    }
    getCustomAttributeName() {
        return this.customFeatureInfoOptions.customAttributeName;
    }
    getCustomAttributeValueFunction() {
        return this.customFeatureInfoOptions.customAttributeValueFunction;
    }
}

class SortFilterConfig {
    constructor(sortFilterConfigOptions) {
        this.hideUnorderedAttributes = true;
        this.layerName = sortFilterConfigOptions.layerName;
        this.tabIndex = sortFilterConfigOptions.tabIndex;
        this.attributeOrder = sortFilterConfigOptions.attributeOrder;
        if (sortFilterConfigOptions.hideUnorderedAttributes !== undefined) {
            this.hideUnorderedAttributes =
                sortFilterConfigOptions.hideUnorderedAttributes;
        }
        this.excludeAttributes = sortFilterConfigOptions.excludeAttributes;
    }
}

/*
 * Public API Surface of ggc-feature-info
 */

/**
 * Generated bundle index. Do not edit.
 */

export { CustomFeatureInfo, FeatureInfoCollection, FeatureInfoComponent, FeatureInfoComponentEvent, FeatureInfoComponentEventType, FeatureInfoConfigService, FeatureInfoDisplayType, FeatureInfoTabsComponent, GgcFeatureInfoModule, SortFilterConfig, ValueTemplateDirective };
